// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { Box, Button, CircularProgress, Dialog, Fade, InputAdornment, MenuItem, Select, TextField, Typography, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { useAppDispatch } from 'Hooks';
import { SetPricingOverrideThunk } from 'Features/admin/adminThunk';
import stylesDef from './styles';

interface PricingOverrideDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  order: IOrder;
  config: IPricingOverrideConfig;
  existingOverride: IPricingOverride | null;
}

const suggestBufferMultiplier = (tiers: IBufferMultiplierTier[], oneSidedBufferFt?: number): number | null => {
  if (oneSidedBufferFt == null || oneSidedBufferFt <= 0) return null;
  const tier = tiers.find((t) => oneSidedBufferFt <= t.maxOneSidedFt);
  return tier ? tier.multiplier : null;
};

// Existing values win; otherwise only the bufferMultiplier prefills, from the catalog-derived tiers.
const buildInitialValues = (
  typeConfig: IPricingOverrideTypeConfig | undefined,
  existingOverride: IPricingOverride | null,
  order: IOrder,
  tiers: IBufferMultiplierTier[],
): Record<string, string> => {
  const values: Record<string, string> = {};
  if (!typeConfig) return values;
  typeConfig.fields.forEach((field) => {
    const existingValue = existingOverride && existingOverride.overrideType === typeConfig.key ? existingOverride.values[field.key] : undefined;
    if (existingValue !== undefined) {
      values[field.key] = String(existingValue);
      return;
    }
    if (field.key === 'bufferMultiplier') {
      const suggestion = suggestBufferMultiplier(tiers, order.oneSidedBufferFt);
      if (suggestion != null) values[field.key] = String(suggestion);
    }
  });
  return values;
};

const formatMoney = (amount: number) => `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const PricingOverrideDialog = ({ open, setOpen, order, config, existingOverride }: PricingOverrideDialogProps) => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();

  const [overrideType, setOverrideType] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const typeConfig = config.types.find((t) => t.key === overrideType);

  useEffect(() => {
    if (!open) return;
    const initialType = existingOverride?.overrideType || '';
    setOverrideType(initialType);
    setValues(buildInitialValues(config.types.find((t) => t.key === initialType), existingOverride, order, config.suggestedBufferMultiplierTiers));
    setNotes(existingOverride?.notes || '');
    setError('');
  }, [open, existingOverride, config, order]);

  const onTypeChange = (key: string) => {
    setOverrideType(key);
    setValues(buildInitialValues(config.types.find((t) => t.key === key), null, order, config.suggestedBufferMultiplierTiers));
    setError('');
  };

  const setFieldValue = (key: string, value: string) => setValues((prev) => ({ ...prev, [key]: value }));

  const bufferHint = (field: IPricingOverrideField): string => {
    if (field.key !== 'bufferMultiplier' || order.oneSidedBufferFt == null) return '';
    const suggestion = suggestBufferMultiplier(config.suggestedBufferMultiplierTiers, order.oneSidedBufferFt);
    return suggestion != null ? `suggested for ${order.oneSidedBufferFt} ft one-sided` : 'custom pricing — no standard multiplier';
  };

  // Cosmetic estimate mirroring the server's three formula evaluators; the server's computation
  // is authoritative and comes back in the save response.
  const previewCost = (): number | null => {
    if (!typeConfig) return null;
    const requiredNumbersOk = typeConfig.fields
      .filter((f) => f.required && f.type === 'number')
      .every((f) => values[f.key] !== undefined && values[f.key] !== '' && Number.isFinite(Number(values[f.key])));
    if (!requiredNumbersOk) return null;

    const multiplier = typeConfig.fields
      .filter((f) => f.role === 'multiplier')
      .reduce((product, f) => {
        const raw = values[f.key];
        if (raw === undefined || raw === '') return product * (f.default ?? 1);
        return product * Number(raw);
      }, 1);
    if (!Number.isFinite(multiplier)) return null;

    if (typeConfig.formula === 'flat') {
      const amountField = typeConfig.fields.find((f) => f.role === 'amount');
      return amountField ? Number(values[amountField.key]) * multiplier : null;
    }
    const unitPriceField = typeConfig.fields.find((f) => f.role === 'unitPrice');
    if (!unitPriceField) return null;
    const unitPrice = Number(values[unitPriceField.key]);
    if (typeConfig.formula === 'rate_times_credits') return unitPrice * (order.creditAcreage || 0);
    if (!typeConfig.quantity) return null;
    const quantity = typeConfig.quantity.source === 'order'
      ? (order[typeConfig.quantity.field as keyof IOrder] as number | undefined)
      : Number(values[typeConfig.quantity.field]);
    if (quantity == null || !Number.isFinite(quantity) || quantity <= 0) return null;
    return unitPrice * quantity * multiplier;
  };

  const handleSave = async () => {
    if (!typeConfig) return;
    setIsLoading(true);
    setError('');
    const typedValues: IPricingOverrideValues = {};
    typeConfig.fields.forEach((field) => {
      const raw = values[field.key];
      if (raw === undefined || raw === '') return;
      typedValues[field.key] = field.type === 'number' ? Number(raw) : raw;
    });
    const result = await dispatch(SetPricingOverrideThunk({ orderId: order._id, overrideType, values: typedValues, notes: notes || undefined }));
    setIsLoading(false);
    if (result.success) {
      setOpen(false);
    } else {
      setError(result.message || 'Unable to save the pricing override');
    }
  };

  const preview = previewCost();

  return (
    <Dialog open={open} onClose={() => setOpen(false)} closeAfterTransition={false}>
      <Box sx={styles.dialogContainer}>
        <Typography variant="h3" sx={styles.dialogTitle}>{existingOverride ? 'Edit Pricing Override' : 'Set Pricing Override'}</Typography>
        <Typography variant="subtitle1">Order-level pricing wins over the org default for invoicing and reporting.</Typography>
        <Box sx={styles.formList}>
          <Select
            value={overrideType}
            onChange={(e) => onTypeChange(e.target.value)}
            displayEmpty
            sx={styles.typeSelect}
          >
            <MenuItem value="" disabled>Select override type</MenuItem>
            {config.types.map((t) => (
              <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>
            ))}
          </Select>
          {typeConfig?.quantity && typeConfig.quantity.source === 'order' && (
            <Typography sx={styles.quantityText}>
              {typeConfig.quantity.label}
              {': '}
              {(order[typeConfig.quantity.field as keyof IOrder] as number | undefined)?.toLocaleString('en-US') ?? 'N/A (not available on this order)'}
            </Typography>
          )}
          {typeConfig?.fields.map((field) => {
            if (field.type === 'select') {
              return (
                <Box key={field.key} sx={styles.fieldRow}>
                  <Typography sx={styles.fieldLabel}>{field.label}</Typography>
                  <Select
                    value={values[field.key] || ''}
                    onChange={(e) => setFieldValue(field.key, e.target.value)}
                    displayEmpty
                    sx={styles.fieldSelect}
                  >
                    {!field.required && <MenuItem value="">—</MenuItem>}
                    {field.options?.map((option) => (
                      <MenuItem key={option} value={option}>{option}</MenuItem>
                    ))}
                  </Select>
                </Box>
              );
            }
            return (
              <Box key={field.key} sx={styles.fieldRow}>
                <Typography sx={styles.fieldLabel}>
                  {field.label}
                  {field.required ? ' *' : ''}
                </Typography>
                <Box sx={styles.fieldInputWrapper}>
                  <TextField
                    type={field.type === 'number' ? 'number' : 'text'}
                    variant="outlined"
                    value={values[field.key] || ''}
                    onChange={(e) => setFieldValue(field.key, e.target.value)}
                    InputProps={field.adornment === '$' ? { startAdornment: <InputAdornment position="start">$</InputAdornment> } : undefined}
                    sx={styles.fieldInput}
                  />
                  {bufferHint(field) && <Typography sx={styles.hintText}>{bufferHint(field)}</Typography>}
                </Box>
              </Box>
            );
          })}
          {typeConfig && (
            <Typography sx={styles.previewText}>
              Estimated cost:
              {' '}
              {preview != null ? formatMoney(preview) : '—'}
            </Typography>
          )}
          <TextField
            label="Notes (internal)"
            multiline
            minRows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            sx={styles.notesInput}
          />
          {error && <Typography sx={styles.errorText}>{error}</Typography>}
        </Box>
        <Box sx={styles.buttonContainer}>
          <Button variant="text" sx={styles.closeButton} onClick={() => setOpen(false)}>Close</Button>
          <Button variant="contained" onClick={handleSave} disabled={isLoading || !overrideType}>
            <Fade in={isLoading}>
              <CircularProgress size={24} sx={styles.buttonProgress} />
            </Fade>
            <Fade in={!isLoading}>
              <span>Save Override</span>
            </Fade>
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};
